import zod from 'zod';
import { stringToBoolean, stringToListOfEnum, stringToListOfNumber, zodLimit, zodPage } from './helpers';

export const occurrencePriorities = [
    'maximum',
    'high',
    'medium',
    'low',
] as const;

export type OccurrencePriorities = typeof occurrencePriorities[number];

export const occurrenceSituations = [
    'pending',
    'in_service',
    'ongoing',
    'concluded'
] as const;

export type OccurrenceSituation = typeof occurrenceSituations[number];

export const triggerTypes = [
    'face_detected',
    'plate_detected',
    'irregular_vehicle',
    'clone_suspect',
    'illegal_debris',
    'area_invasion',
    'scenechangedetection',
    'defocus',
    'mp_plate_detected',
    'mp_mobile_agent_plate_detected',
    'mp_face_detected',
    'alarm_center',
    'guardian_app',
    'school_app',
    'missing_face_detected',
    'sigrc',
    'cortex_plate_detected',
    'plate_detected_by_mobile_agent',
    'plate_detected_by_citizen_app'
] as const;

export type TriggerType = typeof triggerTypes[number];

export const handcuffReasonTypes = [
    'resistance',
    'fearOfEscape',
    'dangerToPhysicalIntegrity',
] as const;

export type HandcuffReasonType = typeof handcuffReasonTypes[number];

export const unitCategoryType = [
    'four_wheels',
    'two_wheels',
    'on_foot',
    'other',
];
export type UnitCategoryType = typeof unitCategoryType[number];

export const unitStatus = [
    // All active will include active/displaced/operationalPause/activeAfterSchedule
    'allActive',
    'active',
    'displaced',
    'operationalPause',
    'waitingActivation',
    'waitingActivationBeforeSchedule',
    'activeAfterSchedule',
    'finished'
] as const;

export type UnitStatus = typeof unitStatus[number];

export const attachmentTypes = [
    'images',
    'videos',
    'documents',
    'others'
] as const;

export type AttachmentType = typeof attachmentTypes[number];

const OccurrencesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    triggerTypeIds: stringToListOfEnum(zod.enum([
        ...triggerTypes,
        'manual',
    ])).optional(),
    priority: zod.enum(occurrencePriorities).optional(),
    situation: zod.enum(occurrenceSituations).optional(),
    occurrenceTypeId: stringToListOfNumber.optional(),
    responsibleUserId: zod.coerce.number().optional(),
    unitNamesIds: stringToListOfNumber.optional(),
    textFilter: zod.string().optional(),
    tags: stringToListOfNumber.optional(),
    actingBodyId: zod.coerce.number().optional(),
    mapPolygonIds: stringToListOfNumber.optional(),
    polygonType: zod.string().optional(),
    assumedBy: zod.coerce.number().optional(),
    transferredBy: zod.coerce.number().optional(),
    receivedBy: zod.coerce.number().optional(),
    finishedBy: zod.coerce.number().optional(),
    beginIn: zod.coerce.number().optional(),
    endIn: zod.coerce.number().optional(),
    actingBodyCommandIds: stringToListOfNumber.optional(),
    withDispatch: stringToBoolean.optional(),
    withoutDispatch: stringToBoolean.optional(),
    withOccurrenceDocument: stringToBoolean.optional(),
    withoutOccurrenceDocument: stringToBoolean.optional(),
    latitude: zod.coerce.number().optional(),
    longitude: zod.coerce.number().optional(),
    radius: zod.coerce.number().optional(),
    displaceReasonIds: stringToListOfNumber.optional(),
    finishReasonCategoryIds: stringToListOfNumber.optional(),
    finishReasonSubCategoryIds: stringToListOfNumber.optional(),
    actingBodyUnitIds: stringToListOfNumber.optional(),
    policeReport: zod.string().optional(),
    cameraTags: stringToListOfNumber.optional(),
    attachmentTypes: zod.array(zod.enum(attachmentTypes)).optional(),
    involvedPersonSearch: zod.string().optional(),
    handcuffUsed: stringToBoolean.optional(),
    arrested: stringToBoolean.optional(),
});

const FinishCategoriesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const PoliceStationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
});

const CityPropertiesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const HandcuffReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    type: zod.enum(handcuffReasonTypes).optional(),
});

const UnitPauseReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitId: zod.coerce.number().optional(),
});

const UnitFinishEarlyReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitId: zod.coerce.number().optional(),
});

const PatrolIndicatorsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const PatrolActivitiesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const OccurrenceTypesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    isActive: stringToBoolean,
});

const ResumeSchema = zod.object({});

const EquipmentsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    unitId: zod.coerce.number().optional(),
});

const TeamNamesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitId: zod.coerce.number().optional(),
    actingBodyCommandIds: stringToListOfNumber.optional(),
    isConnected: stringToBoolean.optional(),
});

const TeamProgramsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const VehicleRelationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const PersonRelationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const ProvidencesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const DispatchReportsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
    tags: stringToListOfNumber.optional(),
});

const UnitDisplacedReasonsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const ObjectsCategoriesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const AssignmentsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const OccurrenceTagsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const ReportTagsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const UnitsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    teamFilter: zod.string().optional(),
    agentFilter: zod.string().optional(),
    status: stringToListOfEnum(zod.enum(unitStatus)).optional(),
    beginIn: zod.coerce.number().optional(),
    endIn: zod.coerce.number().optional(),
    actingBodyId: zod.coerce.number().optional(),
    actingBodyUnitIds: stringToListOfNumber.optional(),
    actingBodyCommandIds: stringToListOfNumber.optional(),
    overdueUnits: stringToBoolean.optional(),
    teamCategoryId: stringToListOfNumber.optional(),
    isConnected: stringToBoolean.optional(),
    agentOnline: stringToBoolean.optional(),
    agentOffline: stringToBoolean.optional(),
});

const PoliceStationFormSchema = zod.object({
    id: zod.coerce.number().optional(),
}).optional();

const CityPropertyFormSchema = zod.object({
    id: zod.coerce.number().optional(),
}).optional();

const TeamNameSchema = zod.object({
    id: zod.coerce.number().optional(),
    selectedTab: zod.enum(['details', 'live']).optional(),
}).optional();

const DispatchOccurrenceDetailSchema = zod.object({
    occurrenceId: zod.coerce.number().optional(),
}).optional();

const EventOperationsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const UnitCategoriesSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    actingBodyId: zod.coerce.number().optional(),
});

const UnitCategorySchema = zod.object({
    id: zod.coerce.number().optional()
}).optional();


export const DispatchSchemas = {
    Occurrences: OccurrencesSchema,
    FinishCategories: FinishCategoriesSchema,
    PoliceStations: PoliceStationsSchema,
    CityProperties: CityPropertiesSchema,
    HandcuffReasons: HandcuffReasonsSchema,
    UnitPauseReasons: UnitPauseReasonsSchema,
    UnitFinishEarlyReasons: UnitFinishEarlyReasonsSchema,
    PatrolIndicators: PatrolIndicatorsSchema,
    PatrolActivities: PatrolActivitiesSchema,
    OccurrenceTypes: OccurrenceTypesSchema,
    Resume: ResumeSchema,
    Equipments: EquipmentsSchema,
    TeamNames: TeamNamesSchema,
    TeamPrograms: TeamProgramsSchema,
    VehicleRelations: VehicleRelationsSchema,
    PersonRelations: PersonRelationsSchema,
    Providences: ProvidencesSchema,
    DispatchReports: DispatchReportsSchema,
    UnitDisplacedReasons: UnitDisplacedReasonsSchema,
    ObjectsCategories: ObjectsCategoriesSchema,
    Assignments: AssignmentsSchema,
    OccurrenceTags: OccurrenceTagsSchema,
    ReportTags: ReportTagsSchema,
    Units: UnitsSchema,
    UnitForm: zod.object({
        id: zod.coerce.number().optional(),
        actingBodyId: zod.string().optional(),
        actingBodyUnitId: zod.string().optional(),
        actingBodyCommandId: zod.string().optional(),
        unitNameId: zod.string().optional(),
    }).optional(),
    PoliceStationForm: PoliceStationFormSchema,
    CityPropertyForm: CityPropertyFormSchema,
    TeamName: TeamNameSchema,
    DispatchOccurrenceDetail: DispatchOccurrenceDetailSchema,
    EventOperations: EventOperationsSchema,
    UnitCategories: UnitCategoriesSchema,
    UnitCategory: UnitCategorySchema
} as const;
